import {
	AlertDialog,
	Anchor,
	FormattedText,
	renderReactDialog
} from "client/Components";
import { useOpenDialog } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { createUrlRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { ReactElement, useCallback } from "react";
import { navigateToWorkspace } from "../Workspace/routing";

export type NoDocumentStoresConfiguredDialogProps = {
	isForAdmin: boolean;
	onClose: (() => void) | "navigate-to-workspace";
};

export const NoDocumentStoresConfiguredDialog = (props: NoDocumentStoresConfiguredDialogProps): ReactElement => {
	const {
		isForAdmin,
		onClose
	} = props;

	const closeHandler = useCallback(
		(): void => {
			if (onClose === "navigate-to-workspace") {
				navigateToWorkspace();
			} else {
				onClose();
			}
		},
		[onClose]
	);

	return (
		<AlertDialog
			trackingId="no-document-stores-configured-dialog"
			title={localize("_DocumentsIsNotConfiguredTitle")}
			confirmText={localize(
				onClose === "navigate-to-workspace"
					? "_BackToWorkspace"
					: "_Close"
			)}
			showCloseButton={false}
			onClose={closeHandler}
		>
			{isForAdmin
				? (
					<FormattedText
						text={localize("_DocumentsIsNotConfiguredAdminBody")}
						thingsToReplace={[
							{
								what: "{ConfigureSharePointLinkPlaceholder}",
								filler: (
									<Anchor
										href={createUrlRelativeToEnvironment("SharePointSettings")}
									>
										{localize("_Here")}
									</Anchor>
								)
							},
							{
								what: "{ConfigureWebDavLinkPlaceholder}",
								filler: (
									<Anchor
										href={createUrlRelativeToEnvironment("WebDavSettings")}
									>
										{localize("_Here")}
									</Anchor>
								)
							}
						]}
					/>
				)
				: localize("_DocumentsIsNotConfiguredUserBody")
			}
		</AlertDialog>
	);
};
NoDocumentStoresConfiguredDialog.displayName = "NoDocumentStoresConfiguredDialog";

export type NoDocumentStoresConfiguredDialogInitialData = Omit<NoDocumentStoresConfiguredDialogProps, "onClose"> & {
	onClose?: "navigate-to-workspace";
};

export function useNoDocumentStoresConfiguredDialog(): [
	element: ReactElement | null,
	open: (props: NoDocumentStoresConfiguredDialogInitialData) => Promise<void>
] {
	return useOpenDialog<NoDocumentStoresConfiguredDialogInitialData>(
		(props, onClose) => {
			const { onClose: onCloseFromProps } = props;
			return (
				<NoDocumentStoresConfiguredDialog
					{...props}
					onClose={
						typeof onCloseFromProps === "string"
							? onCloseFromProps
							: onClose
					}
				/>
			);
		}
	);
}

export function openNoDocumentStoresConfiguredDialog(isForAdmin: boolean): void {
	renderReactDialog((onClose) => (
		<NoDocumentStoresConfiguredDialog
			isForAdmin={isForAdmin}
			onClose={onClose}
		/>
	));
}
